import { defineStore } from 'pinia'
import { ref, computed, unref, watchEffect } from 'vue'
import { qs } from './qs'

export function createCrudStore(name, api, columns = []) {
    return defineStore(name, () => {

        /* ---------------------------
         * STATE
         * --------------------------- */

        const list = ref([])
        const byId = ref(new Map())

        const totalCount = ref(0)
        const currentPage = ref(1)
        const pageSize = ref(24)

        const currentFilters = ref({})
        const currentSort = ref({ field: null, direction: 'asc' })

        const infiniteScroll = ref(false)

        const pending = ref(0)
        const loading = computed(() => pending.value > 0)
        const error = ref(null)

        let requestId = 0
        const inflight = new Map()

        /* ---------------------------
         * INTERNAL HELPERS
         * --------------------------- */

        const start = () => pending.value++
        const end = () => pending.value--

        const makeKey = (id, section=null) =>
            section ? `${section}:${id}` : id

        function setItem(key, item) {
            const existing = byId.value.get(key)
            if (existing) Object.assign(existing, item)
            else byId.value.set(key, item)
        }

        function normalize(items, section=null) {
            items?.forEach(item => {
                if (item.id !== undefined) setItem(makeKey(item.id, section), item)
            })
        }

        async function safe(fn) {
            start()
            error.value = null
            try {
                return await fn()
            } catch (e) {
                error.value = e
                console.error(e)
                throw e
            } finally {
                end()
            }
        }

        /* ---------------------------
         * FETCH LIST
         * --------------------------- */

        async function fetchPage(
            page = 1,
            filters = {},
            sort = null,
            section = null
        ) {
            const rid = ++requestId

            currentPage.value = page
            currentFilters.value = filters
            if (sort) currentSort.value = sort

            return safe(async () => {

                const query = qs(
                    filters,
                    {
                        page,
                        pageSize: pageSize.value,
                        sort: currentSort.value
                    },
                    columns
                )

                const data = section
                    ? await api.list(query, section)
                    : await api.list(query)

                if (rid !== requestId) return list.value

                normalize(data.results, section)

                if (infiniteScroll.value && page > 1) {
                    const seen = new Set(list.value.map(i => i.id))
                    list.value.push(
                        ...data.results.filter(i => !seen.has(i.id))
                    )
                } else {
                    list.value = data.results
                }

                totalCount.value = data.count

                return list.value
            })
        }

        async function fetchNextPage(section=null) {
            if (list.value.length >= totalCount.value) return
            return fetchPage(
                currentPage.value + 1,
                currentFilters.value,
                currentSort.value,
                section
            )
        }

        async function refresh(filters={}, sort=null, section=null) {
            return fetchPage(1, filters, sort, section)
        }

        /* ---------------------------
         * FETCH ONE (DEDUPED)
         * --------------------------- */

        async function fetchOne(id, section=null, refresh=false) {

            const key = makeKey(id, section)

            if (!refresh && byId.value.has(key))
                return byId.value.get(key)

            if (inflight.has(key))
                return inflight.get(key)

            const promise = safe(async () => {
                const item = section
                    ? await api.retrieve(id, section)
                    : await api.retrieve(id)

                if (key !== undefined) setItem(key, item)
                return item
            })

            inflight.set(key, promise)

            try {
                return await promise
            } finally {
                inflight.delete(key)
            }
        }

        /* ---------------------------
         * SAVE (CREATE / UPDATE)
         * --------------------------- */

        async function save(data, id=null, section=null) {

            return safe(async () => {

                const item = section
                    ? id
                        ? await api.update(id, data, section)
                        : await api.create(data, section)
                    : id
                        ? await api.update(id, data)
                        : await api.create(data)

                const key = makeKey(item.id, section)
                if (key !== undefined) setItem(key, item)

                return item
            })
        }

        /* ---------------------------
         * DELETE
         * --------------------------- */

        async function destroy(ids, section=null) {

            const idArray = Array.isArray(ids) ? ids : [ids]

            return safe(async () => {

                await Promise.all(
                    idArray.map(id =>
                        section
                            ? api.destroy(id, section)
                            : api.destroy(id)
                    )
                )

                idArray.forEach(id =>
                    byId.value.delete(makeKey(id, section))
                )
            })
        }

        /* ---------------------------
         * UPDATE FLAG
         * --------------------------- */

        async function updateFlag(id, flagKey, value=true, section=null) {

            const key = makeKey(id, section)
            const item = byId.value.get(key)
            if (!item) return null

            const prev = item[flagKey]
            item[flagKey] = value

            try {
                await save({ [flagKey]: value }, id, section)
            } catch (e) {
                item[flagKey] = prev
                throw e
            }

            return item
        }

        /* ---------------------------
         * REACTIVE ITEM FETCH
         * --------------------------- */

        function useItemFetch(id, section=null) {

            const itemRef = computed(() =>
                byId.value.get(makeKey(unref(id), section)) || null
            )

            watchEffect(() => {
                const val = unref(id)
                if (!val) return
                if (!byId.value.has(makeKey(val, section)))
                    fetchOne(val, section)
            })

            return itemRef
        }

        /* ---------------------------
         * CUSTOM QUERY
         * --------------------------- */

        async function query(id=null, endpoint=null, body=null) {
            return safe(() => api.query(id, endpoint, body))
        }

        /* ---------------------------
         * CUSTOM QUERY
         * --------------------------- */

        async function get(id=null, section=null, endpoint=null, params=null, body=null) {
            return safe(async () => {
                // section
                //     ? api.retrieve(id, section)
                //     : api.retrieve(id, '')

                const query = qs(
                    {},
                    {
                        page: 1,
                        pageSize: 99999999
                    },
                    null
                )

                const data = section
                    ? await api.list(query, section)
                    : await api.list(query)

                normalize(data.results, section)
                
                list.value = data.results
                totalCount.value = data.count

                return list.value
            })
        }

        /* ---------------------------
         * RESET
         * --------------------------- */

        function reset() {
            list.value = []
            byId.value = new Map()
            totalCount.value = 0
            currentPage.value = 1
            currentFilters.value = {}
            currentSort.value = { field:null, direction:'asc' }
            requestId++
        }

        /* ---------------------------
         * PUBLIC API (UNCHANGED)
         * --------------------------- */

        return {
            list,
            byId,
            loading,
            error,
            infiniteScroll,
            totalCount,
            currentPage,
            pageSize,
            currentFilters,
            currentSort,

            fetchPage,
            fetchNextPage,
            refresh,
            reload: refresh,

            fetchOne,
            save,
            destroy,
            updateFlag,
            useItemFetch,
            query,
            get,
            reset,
        }
    })
}